import React from 'react';

import { Box, Stack } from '@mui/material';
import { Skeleton } from '@repo/ui';

import { border, font, space } from 'settings/tokens';

/**
 * What sits behind the access gate: the SHAPE of the certificate's details, blurred.
 *
 * ## ⚠️ PLACEHOLDERS, not the real details with a filter over them
 *
 * This is the load-bearing decision in the whole gate, so it is stated first.
 *
 * `GET /certificates/public/:id` returns the ENTIRE certificate to an anonymous caller —
 * `description`, `status`, `scan_counter`, `tag_id`, and the full `product` object with
 * its title, sku and details. Only `customer` comes back null. Verified against the live
 * payload; the gate is a UI curtain, not a security boundary, and the API is where a real
 * one would have to go.
 *
 * That makes CSS-blurring the real content strictly WEAKER than not rendering it. The
 * words stay in the DOM, so select-all, reader mode, "view source", a text-only browser,
 * or deleting one declaration in devtools all reveal them — and the first three need no
 * developer tools at all. A blur over real text looks exactly like protection and is not,
 * which is the worst of the three available outcomes.
 *
 * Blurred text is unreadable BY CONSTRUCTION, so a placeholder at the same footprint is
 * visually indistinguishable from blurred real content and cannot be un-blurred into
 * anything. The curtain is honest: there is genuinely nothing behind it to find.
 *
 * ## What it mirrors
 *
 * `ProductTabContent`'s public branch, at its footprint (§19): the DETAILS caption, the
 * `ProductDetailsPanel` label/value rows with their hairlines (`py: space.md`, 14px text
 * at 1.5 → ~45px a row), then the Description field label and its prose. Bars in the real
 * rows' positions, not a generic block parked in the middle of an empty page — the point
 * is to read as "the certificate, obscured", which a block does not.
 *
 * It does NOT mirror the whole page. The hero, the title and the state cards are ungated
 * and render for real above this; only what the gate withholds is drawn here.
 */
export const GatedDetailsPlaceholder: React.FC = () => (
  <Box
    /*
     * DECORATIVE, and the three attributes say so to the three audiences that ask.
     *
     * `aria-hidden` — a screen-reader user gets the gate card's heading, description and
     * button, which is the entire message. Announcing twelve unlabelled placeholder bars
     * after it would be pure noise, and worse, it would imply there is content here to
     * navigate. There is not.
     *
     * `pointerEvents: none` — nothing inside is interactive, so this also keeps the region
     * from swallowing a tap aimed at the page behind it.
     *
     * `userSelect: none` — a drag-select across the page must not appear to catch anything
     * here. There are no text nodes to catch, so this changes no outcome; it exists so the
     * region cannot be MISTAKEN for real content by someone probing it, which is exactly
     * what a reader who suspects a CSS blur would try first.
     *
     * Nothing inside is focusable (every child is a `Box`), so the keyboard skips it
     * without needing `inert` — which is worth having as a property of the markup rather
     * than of an attribute, since `inert` is still uneven across the browsers this app
     * runs in (it is reached by tapping a tag with a phone).
     */
    aria-hidden='true'
    sx={theme => ({
      pt: 3,
      pointerEvents: 'none',
      userSelect: 'none',
      /*
       * ── ON A PHONE THIS IS A FLEX FILLER, NOT A FIXED BLOCK ──────────────────────
       *
       * It was 335px of authored height, and 335 was almost exactly the page's
       * overflow: measured at 375x812 the document ran to 1188, so "Powered by Vera"
       * landed at y=1127, some 315px below the fold, and the gated view — which shows
       * no real content at all — was a scrolling page. A curtain that makes you scroll
       * past it to reach the end of a page with nothing on it is not implying depth,
       * it is spending the visitor's viewport on a decoration.
       *
       * So the height is now WHATEVER IS LEFT between the gate card and the footer.
       * `flex: 1 1 0` with `minHeight: 0` lets it take the slack on a tall screen and
       * give it all back on a short one; `overflow: hidden` clips the skeleton rows it
       * cannot fit rather than forcing the page taller. The footer then sits at the
       * bottom of the first screen, which is what was asked for.
       *
       * ⚠️ It can legitimately resolve to ZERO, and that is the rule working, not
       * failing. At 375x812 with BOTH email and location collected there is genuinely
       * no room: hero 375 + title + a 4-field card + footer already exceeds 812, which
       * is why the card was trimmed alongside this. On the location-only gate, and on
       * any viewport past ~860, it gets real space and the fade does its job.
       *
       * ── md+ KEEPS THE FIXED HEIGHT, and that is deliberate ───────────────────────
       *
       * The desktop grid sets `alignItems: 'start'` (required, or the sticky media
       * column has no room to travel), so the record column is sized by its content
       * and has no free space to distribute. `flex: 1 1 0` there would resolve to 0
       * and delete the blur outright on every desktop viewport. The complaint is a
       * phone complaint; the fix stays on the phone.
       */
      [theme.breakpoints.down('md')]: {
        flex: '1 1 0',
        minHeight: 0,
        overflow: 'hidden',
        // The card above already carries `mb: 2` from `stateCardSurfaceSx`, so this
        // was a second gap stacked on the first — 40px between the card and the blur,
        // and 24px of un-shrinkable padding inside a box whose whole job is now to
        // shrink. One gap, owned by the card.
        pt: 0,
      },
      /*
       * 5px. Below ~3px individual bars keep a crisp edge and the region reads as
       * content that failed to load; above ~8px the row rhythm dissolves into one grey
       * smear and it reads as a rendering fault. At 5 the STRUCTURE survives — you can
       * see a caption, rows, a paragraph — while nothing in it resolves. That structure
       * is the whole argument for a blur over an empty space: it says "a certificate is
       * here" rather than "nothing is here".
       */
      filter: 'blur(5px)',
      /*
       * Recessive, and NOT for decoration. The gate card immediately above is the page's
       * only action, and at full strength the blurred block below competes with it for
       * attention while offering nothing to do.
       */
      opacity: 0.55,
      /*
       * FADE, rather than a hard bottom edge.
       *
       * A blurred block that stops dead reads as a broken render — the eye takes the cut
       * as damage rather than as an end. Dissolving says "this continues", which is both
       * true and the thing a paywall wants to imply.
       *
       * It also bounds the dead zone. Without a cap the placeholder would be as tall as
       * whatever it mirrors, so the visitor scrolls a screenful of grey to reach the
       * footer. The mask starts at 45% so the top half is fully legible AS STRUCTURE
       * before it starts to go.
       *
       * `WebkitMaskImage` alongside `maskImage`: iOS Safari is the modal browser here
       * (the app is reached by tapping an NFC tag with a phone) and still wants the
       * prefix on some versions in the field.
       */
      maskImage: 'linear-gradient(to bottom, #000 0%, #000 45%, transparent 100%)',
      WebkitMaskImage: 'linear-gradient(to bottom, #000 0%, #000 45%, transparent 100%)',
    })}
  >
    <Stack spacing={2}>
      {/* The DETAILS caption. Uppercase 14px at 0.06em tracking, so a short bar. */}
      <Skeleton width={64} height={14} animation={false} />

      {/* `ProductDetailsPanel`'s rows: label left, value right, hairline between, none
          under the last. Four is what a typical certificate carries (Certificate ID,
          Product ID, Manufacture date, Owned since). */}
      <Stack>
        {[
          { label: '38%', value: '46%' },
          { label: '30%', value: '28%' },
          { label: '42%', value: '34%' },
          { label: '34%', value: '30%' },
        ].map((row, idx, rows) => (
          <Stack
            key={row.label + idx}
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            sx={{
              gap: `${space.lg}px`,
              py: `${space.md}px`,
              // `!important` for the same reason the real panel needs it: the app's
              // `#vera-shadow-root * { border-color: … }` rule carries ID specificity
              // and silently discards an sx border colour without it (settings/tokens).
              ...(idx < rows.length - 1
                ? { borderBottom: `1px solid ${border.subtle} !important` }
                : null),
            }}
          >
            <Skeleton width={row.label} height={font.label} animation={false} />
            <Skeleton width={row.value} height={font.label} animation={false} />
          </Stack>
        ))}
      </Stack>

      {/* The Description field: its label, then the page's one run of prose. */}
      <Stack spacing={1}>
        <Skeleton width={84} height={font.label} animation={false} />
        <Skeleton lines={4} height={font.body} animation={false} />
      </Stack>
    </Stack>
  </Box>
);

export default GatedDetailsPlaceholder;
